// =============================================================================
// MeuDriver — InstructorAnnouncementCard
//
// Exibe o anúncio ativo do instrutor no topo do perfil público.
// Busca o anúncio mais recente do instructor_id passado.
// Texto limitado a 200 chars (controlado no banco via CHECK).
// =============================================================================

import { Megaphone } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

interface InstructorAnnouncementCardProps {
  instructorId: string;
}

interface AnnouncementRow {
  id: string;
  text: string;
  active_until: string | null;
}

async function fetchActiveAnnouncement(
  instructorId: string,
): Promise<AnnouncementRow | null> {
  const { data, error } = await supabase
    .from("instructor_announcements")
    .select("id, text, active_until")
    .eq("instructor_id", instructorId)
    .or("active_until.is.null,active_until.gt." + new Date().toISOString())
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("[InstructorAnnouncementCard] fetch error:", error.message);
    return null;
  }
  return data ?? null;
}

export function InstructorAnnouncementCard({
  instructorId,
}: InstructorAnnouncementCardProps) {
  const { data: announcement, isLoading } = useQuery({
    queryKey: ["instructor-announcement", instructorId],
    queryFn: () => fetchActiveAnnouncement(instructorId),
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading || !announcement) return null;

  return (
    <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 shadow-sm">
      <Megaphone className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" aria-hidden />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-800 mb-1">Aviso do instrutor</p>
        <p className="text-sm text-amber-700 leading-relaxed break-words">
          {announcement.text}
        </p>
        {announcement.active_until && (
          <p className="text-xs text-amber-500 mt-1.5">
            Válido até{" "}
            {format(parseISO(announcement.active_until), "d 'de' MMMM", {
              locale: ptBR,
            })}
          </p>
        )}
      </div>
    </div>
  );
}
